import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Popover,
  Button,
  IconButton,
} from "@mui/material";
import {
  HealthAndSafety,
  Watch,
  FitnessCenter,
  LocalDining,
  Pool,
  SelfImprovement,
  Biotech,
  Looks,
  MonitorHeart,
  AttachMoney,
  Mood,
  Visibility,
  Hearing,
  Restaurant,
  Warning,
  DirectionsWalk,
  EmojiEvents,
  LocalHospital,
  Diversity3,
  MedicalInformation,
  HealthAndSafetyOutlined,
  SafetyCheck,
} from "@mui/icons-material";

interface Benefit {
  id: number;
  benefit_name: string;
  benefit_type: string;
  description: string;
  assigned_to: Array<{ id: string; codename: string; gender: string }>;
}

interface BenefitsTableProps {
  benefits: Benefit[];
  onRecommendBenefit: (
    benefitId: number | null,
    memberId: string
  ) => Promise<void>;
}

const getBenefitIcon = (benefitType: string) => {
  switch (benefitType) {
    // New categories added at the top
    case "SDoH":
      return <Diversity3 />;
    case "Health":
      return <MedicalInformation />;
    case "DVH":
      return <HealthAndSafetyOutlined />;
    case "Safety":
      return <SafetyCheck />;
    // Existing categories
    case "Insurance":
      return <HealthAndSafety />;
    case "Wearable":
      return <Watch />;
    case "Lesson":
      return <FitnessCenter />;
    case "Diet":
      return <LocalDining />;
    case "Gym":
      return <FitnessCenter />;
    case "Analysis":
      return <Biotech />;
    case "Styling":
      return <Looks />;
    case "Monitoring":
      return <MonitorHeart />;
    case "Flex":
      return <AttachMoney />;
    case "Dental":
      return <Mood />;
    case "Vision":
      return <Visibility />;
    case "Hearing":
      return <Hearing />;
    case "Meal":
      return <Restaurant />;
    case "Emergency":
      return <Warning />;
    case "Foot Care":
      return <DirectionsWalk />;
    case "Reward Program":
      return <EmojiEvents />;
    case "Medicaid":
      return <LocalHospital />;
    default:
      return <HealthAndSafety />;
  }
};

const BenefitsTable: React.FC<BenefitsTableProps> = ({
  benefits,
  onRecommendBenefit,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    benefit: Benefit
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedBenefit(benefit);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedBenefit(null);
  };

  const open = Boolean(anchorEl);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#F1EFFD",
            }}
          >
            <TableCell sx={{ fontWeight: "bold" }}>Benefit</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Assigned To</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {benefits.map((benefit) => (
            <TableRow key={benefit.id}>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton size="small" sx={{ mr: 1, color: "#5958e1" }}>
                    {getBenefitIcon(benefit.benefit_type)}
                  </IconButton>
                  {benefit.benefit_name}
                </Box>
              </TableCell>
              <TableCell>{benefit.benefit_type}</TableCell>
              <TableCell>{benefit.description}</TableCell>
              <TableCell>
                <AvatarGroup max={3}>
                  {benefit.assigned_to &&
                    benefit.assigned_to.map((member, index) => (
                      <Avatar key={index} sx={{ width: 30, height: 30 }} />
                    ))}
                </AvatarGroup>
                {benefit.assigned_to && benefit.assigned_to.length > 0 && (
                  <Button
                    size="small"
                    onClick={(e) => handlePopoverOpen(e, benefit)}
                  >
                    {benefit.assigned_to.length} assigned
                  </Button>
                )}
              </TableCell>
              <TableCell>
                <Button onClick={() => onRecommendBenefit(benefit.id, "")}>
                  Recommend
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {selectedBenefit && (
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Assigned To
            </Typography>
            {selectedBenefit.assigned_to.map((member, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <Avatar sx={{ mr: 1 }} />
                <Typography>{member.codename}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </TableContainer>
  );
};

export default BenefitsTable;
